import { useAuth0 } from "@auth0/auth0-react";
import { isEmpty } from "lodash-es";
import { useRouter } from "next/router";
import { useContext, useMemo } from "react";

import { LoadingLayout } from "../../components/layout/LoadingLayout";
import { Redirect } from "../../components/Redirect";
import { IUseOperationContextOpts, useOperationContext } from "../../hooks/useOperationContext";
import {
    OmiAuthenticatedContextProvider,
    OmiUnauthenticatedContextProvider,
} from "../../omi/contexts/OmiAuthContextProvider";
import { OmiLoading } from "../../omi/ui/layout/OmiLoading";
import { Routes } from "../../Routes";
import { UserAnalyticsServicesProvider } from "../../services/UserAnalyticsServicesProvider";
import { NO_LEVRO_ORG_KEY_HEADERS_INIT } from "../../utils/getRequestHeaders";
import { visitQueryState } from "../../utils/visitQueryState";
import { CurrentUserContextProvider } from "../current-user/CurrentUserContext";
import { CustomerContext, CustomerContextProvider } from "../customer/CustomerContext";
import { FeatureFlagsContextProvider } from "../feature-flags/FeatureFlagsContext";
import { PermissionsContextProvider } from "../permission/PermissionsContext";
import {
    INonAuthedAppContextProviderQuery,
    IYesAuthedAppContextProviderQuery,
    IYesAuthedCustomerAppContextProviderQuery,
    useNonAuthedAppContextProviderQuery,
    useYesAuthedAppContextProviderQuery,
    useYesAuthedCustomerAppContextProviderQuery,
} from "./AuthedAppContextProvider.generated";

const EMPTY_ARRAY: [] = [];
const OMI_MODE = Boolean(parseInt(process.env.NEXT_PUBLIC_OMI_MODE ?? "0"));

/**
 * Provides a set of contexts depending on if the user is logged in and has an account set up.
 */
export const AuthedAppContextProvider: React.FC<React.PropsWithChildren> = (props) => {
    const { isLoading, isAuthenticated } = useAuth0();
    const { pathname } = useRouter();

    const isOmi = pathname.startsWith(Routes.Omi()) || OMI_MODE;

    if (isLoading) {
        return isOmi ? <OmiLoading /> : <LoadingLayout />;
    }

    if (isAuthenticated && !pathname.startsWith(Routes.KycApply())) {
        return <YesAuthedAppContextProvider isOmi={isOmi} {...props} />;
    }

    return isOmi ? <OmiUnauthenticatedContextProvider {...props} /> : <NonAuthedAppContextProvider {...props} />;
};

const YES_AUTHED_OPERATION_CONTEXT_OPTS: IUseOperationContextOpts<IYesAuthedAppContextProviderQuery> = {
    additionalTypenameFlags: { ApplicationSummary: true, Customer: true },
};

const YesAuthedAppContextProvider: React.FC<React.PropsWithChildren<{ isOmi: boolean }>> = ({ children, isOmi }) => {
    const operationContext = useOperationContext(YES_AUTHED_OPERATION_CONTEXT_OPTS);
    const context = useMemo(
        () => ({ ...operationContext, fetchOptions: { headers: NO_LEVRO_ORG_KEY_HEADERS_INIT } }),
        [operationContext],
    );
    const [queryState] = useYesAuthedAppContextProviderQuery({ context });

    function renderLoading() {
        return isOmi ? <OmiLoading /> : <LoadingLayout />;
    }

    function renderError() {
        return (
            <UserAnalyticsServicesProvider role={undefined} orgId={undefined}>
                <FeatureFlagsContextProvider activeFeatureFlags={EMPTY_ARRAY}>{children}</FeatureFlagsContextProvider>
            </UserAnalyticsServicesProvider>
        );
    }

    function renderLoaded({
        getApplications: { applications },
        getCurrentUserCustomers: { customers },
    }: IYesAuthedAppContextProviderQuery) {
        if (isEmpty(applications) && isEmpty(customers) && !isOmi) {
            return <Redirect to={Routes.KycApply()} />;
        }
        if (applications.length === 1 && isEmpty(customers) && !isOmi) {
            return <Redirect to={Routes.KycApply.Application({ applicationId: applications[0].id })} />;
        }

        return (
            <CustomerContextProvider
                applications={applications ?? undefined}
                customers={customers ?? undefined}
                isOmi={isOmi}
            >
                {isOmi ? (
                    <OmiAuthenticatedContextProvider>{children}</OmiAuthenticatedContextProvider>
                ) : (
                    <YesAuthedCustomerAppContextProvider>{children}</YesAuthedCustomerAppContextProvider>
                )}
            </CustomerContextProvider>
        );
    }

    return visitQueryState(queryState, {
        notLoaded: renderLoading,
        loaded: ({ data }) => renderLoaded(data),
        error: renderError,
    });
};

const YES_AUTHED_CUSTOMER_OPERATION_CONTEXT_OPTS: IUseOperationContextOpts<IYesAuthedCustomerAppContextProviderQuery> =
    {
        additionalTypenameFlags: {},
    };

const YesAuthedCustomerAppContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [queryState] = useYesAuthedCustomerAppContextProviderQuery({
        context: useOperationContext(YES_AUTHED_CUSTOMER_OPERATION_CONTEXT_OPTS),
    });

    const { selectedCustomer } = useContext(CustomerContext);

    function renderLoading() {
        return <LoadingLayout />;
    }

    function renderError() {
        return (
            <UserAnalyticsServicesProvider role={undefined} orgId={undefined}>
                <FeatureFlagsContextProvider activeFeatureFlags={EMPTY_ARRAY}>{children}</FeatureFlagsContextProvider>
            </UserAnalyticsServicesProvider>
        );
    }

    function renderLoaded({
        getCurrentUser: { user },
        getUserPermissions: { permissions },
        getActiveFeatures,
    }: IYesAuthedCustomerAppContextProviderQuery) {
        return (
            <CurrentUserContextProvider currentUser={user ?? undefined}>
                <UserAnalyticsServicesProvider
                    role={user?.restrictedDetails?.rolesInfo?.role ?? undefined}
                    orgId={selectedCustomer?.id}
                >
                    <PermissionsContextProvider permissions={permissions}>
                        <FeatureFlagsContextProvider activeFeatureFlags={getActiveFeatures}>
                            {children}
                        </FeatureFlagsContextProvider>
                    </PermissionsContextProvider>
                </UserAnalyticsServicesProvider>
            </CurrentUserContextProvider>
        );
    }

    return visitQueryState(queryState, {
        notLoaded: renderLoading,
        loaded: ({ data }) => renderLoaded(data),
        error: renderError,
    });
};

const NON_AUTHED_OPERATION_CONTEXT_OPTS: IUseOperationContextOpts<INonAuthedAppContextProviderQuery> = {
    additionalTypenameFlags: {},
};

const NonAuthedAppContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [queryState] = useNonAuthedAppContextProviderQuery({
        context: useOperationContext(NON_AUTHED_OPERATION_CONTEXT_OPTS),
    });

    function render(gqlQuery?: INonAuthedAppContextProviderQuery) {
        return (
            <UserAnalyticsServicesProvider role={undefined} orgId={undefined}>
                <FeatureFlagsContextProvider activeFeatureFlags={gqlQuery?.getActiveFeatures}>
                    {children}
                </FeatureFlagsContextProvider>
            </UserAnalyticsServicesProvider>
        );
    }

    return visitQueryState(queryState, {
        notLoaded: () => <LoadingLayout />,
        loaded: ({ data: gqlQuery }) => render(gqlQuery),
        error: () => render(),
    });
};
